// TMS Design System - Extended Color Palette for Chakra UI v3
// This file provides extended color scales for all brand and semantic colors
// Note: Color values are hardcoded to avoid circular dependency issues

// Helper function to generate color scale from base color
// const generateColorScale = (_baseColor: string, _name: string) => {
//   // This is a simplified color scale generator
//   // In production, you might want to use a color manipulation library
//   return {
//     50: { value: `${_baseColor}10` },
//     100: { value: `${_baseColor}20` },
//     200: { value: `${_baseColor}30` },
//     300: { value: `${_baseColor}50` },
//     400: { value: `${_baseColor}70` },
//     500: { value: _baseColor },
//     600: { value: `${_baseColor}CC` },
//     700: { value: `${_baseColor}AA` },
//     800: { value: `${_baseColor}88` },
//     900: { value: `${_baseColor}66` },
//     950: { value: `${_baseColor}44` },
//   };
// };

// Extended color palettes
export const colorPalettes = {
  // Gray scale (neutral colors)
  gray: {
    50: { value: '#FFFFFF' },
    100: { value: '#F8F9FA' },
    200: { value: '#E9ECEF' },
    300: { value: '#DEE2E6' },
    400: { value: '#CED4DA' },
    500: { value: '#ADB5BD' },
    600: { value: '#6C757D' },
    700: { value: '#495057' },
    800: { value: '#343A40' },
    900: { value: '#212529' },
    950: { value: '#0A0A0A' },
  },

  // Purple scale (for secondary brand color)
  purple: {
    50: { value: '#FAF5FF' },
    100: { value: '#F3E8FF' },
    200: { value: '#E9D5FF' },
    300: { value: '#D8B4FE' },
    400: { value: '#C084FC' },
    500: { value: '#A855F7' },
    600: { value: '#6F42C1' },
    700: { value: '#6B21A8' },
    800: { value: '#581C87' },
    900: { value: '#3B0764' },
    950: { value: '#1E0533' },
  },

  // Orange scale (for accent and express logistics)
  orange: {
    50: { value: '#FFF7ED' },
    100: { value: '#FFEDD5' },
    200: { value: '#FED7AA' },
    300: { value: '#FDBA74' },
    400: { value: '#FB923C' },
    500: { value: '#FF6B35' },
    600: { value: '#EA580C' },
    700: { value: '#C2410C' },
    800: { value: '#9A3412' },
    900: { value: '#7C2D12' },
    950: { value: '#431407' },
  },

  // Teal scale (for highlights)
  teal: {
    50: { value: '#F0FDFA' },
    100: { value: '#CCFBF1' },
    200: { value: '#99F6E4' },
    300: { value: '#5EEAD4' },
    400: { value: '#2DD4BF' },
    500: { value: '#20C997' },
    600: { value: '#0D9488' },
    700: { value: '#0F766E' },
    800: { value: '#115E59' },
    900: { value: '#134E4A' },
    950: { value: '#042F2E' },
  },

  // Red scale (danger/error states)
  red: {
    50: { value: '#FEF2F2' },
    100: { value: '#FEE2E2' },
    200: { value: '#FECACA' },
    300: { value: '#FCA5A5' },
    400: { value: '#F87171' },
    500: { value: '#DC3545' },
    600: { value: '#DC2626' },
    700: { value: '#B91C1C' },
    800: { value: '#991B1B' },
    900: { value: '#7F1D1D' },
    950: { value: '#450A0A' },
  },

  // Green scale (success states)
  green: {
    50: { value: '#F0FDF4' },
    100: { value: '#DCFCE7' },
    200: { value: '#BBF7D0' },
    300: { value: '#86EFAC' },
    400: { value: '#4ADE80' },
    500: { value: '#28A745' },
    600: { value: '#16A34A' },
    700: { value: '#15803D' },
    800: { value: '#166534' },
    900: { value: '#14532D' },
    950: { value: '#052E16' },
  },

  // Yellow scale (warning states)
  yellow: {
    50: { value: '#FFFBEB' },
    100: { value: '#FEF3C7' },
    200: { value: '#FDE68A' },
    300: { value: '#FCD34D' },
    400: { value: '#FBBF24' },
    500: { value: '#FFC107' },
    600: { value: '#D97706' },
    700: { value: '#B45309' },
    800: { value: '#92400E' },
    900: { value: '#78350F' },
    950: { value: '#451A03' },
  },

  // Blue scale (info states and standard logistics)
  blue: {
    50: { value: '#EFF6FF' },
    100: { value: '#DBEAFE' },
    200: { value: '#BFDBFE' },
    300: { value: '#93C5FD' },
    400: { value: '#60A5FA' },
    500: { value: '#3B82F6' },
    600: { value: '#2563EB' },
    700: { value: '#1D4ED8' },
    800: { value: '#1E40AF' },
    900: { value: '#1E3A8A' },
    950: { value: '#172554' },
  },
};

// Logistics-specific color definitions
export const logisticsColors = {
  economy: {
    solid: { value: '#CED4DA' },
    muted: { value: '#F8F9FA' },
    subtle: { value: '#FFFFFF' },
    contrast: { value: '#212529' },
  },
  standard: {
    solid: { value: colorPalettes.blue[500].value },
    muted: { value: colorPalettes.blue[100].value },
    subtle: { value: colorPalettes.blue[50].value },
    contrast: { value: '#FFFFFF' },
  },
  express: {
    solid: { value: colorPalettes.orange[500].value },
    muted: { value: colorPalettes.orange[100].value },
    subtle: { value: colorPalettes.orange[50].value },
    contrast: { value: '#FFFFFF' },
  },
  cargo: {
    solid: { value: colorPalettes.purple[600].value },
    muted: { value: colorPalettes.purple[100].value },
    subtle: { value: colorPalettes.purple[50].value },
    contrast: { value: '#FFFFFF' },
  },
};

// Status-specific color definitions
export const statusColors = {
  pending: {
    solid: { value: colorPalettes.yellow[500].value },
    muted: { value: colorPalettes.yellow[100].value },
    subtle: { value: colorPalettes.yellow[50].value },
    contrast: { value: '#212529' },
  },
  confirmed: {
    solid: { value: colorPalettes.blue[500].value },
    muted: { value: colorPalettes.blue[100].value },
    subtle: { value: colorPalettes.blue[50].value },
    contrast: { value: '#FFFFFF' },
  },
  'in-transit': {
    solid: { value: colorPalettes.orange[500].value },
    muted: { value: colorPalettes.orange[100].value },
    subtle: { value: colorPalettes.orange[50].value },
    contrast: { value: '#FFFFFF' },
  },
  delivered: {
    solid: { value: colorPalettes.green[500].value },
    muted: { value: colorPalettes.green[100].value },
    subtle: { value: colorPalettes.green[50].value },
    contrast: { value: '#FFFFFF' },
  },
  cancelled: {
    solid: { value: colorPalettes.red[500].value },
    muted: { value: colorPalettes.red[100].value },
    subtle: { value: colorPalettes.red[50].value },
    contrast: { value: '#FFFFFF' },
  },
  delayed: {
    solid: { value: colorPalettes.orange[600].value },
    muted: { value: colorPalettes.orange[200].value },
    subtle: { value: colorPalettes.orange[100].value },
    contrast: { value: '#FFFFFF' },
  },
};
